import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DownCaretIcon, TextInput, WarningIcon } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import { addDays } from 'date-fns';
import pick from 'lodash/pick';
import { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import { generateKeysAndUserId } from '../../engine/helpers/generateKeysAndUserId';
import { generateSessionId, validateScheduledSession, } from '../../services/schedule/ScheduledSession';
import { ScheduleService } from '../../services/schedule/ScheduleService';
import { Popup } from '../../utils/popups';
import { generateStreamId } from '../../utils/streamId';
import { formatDate } from '../../utils/time';
import { BroadcastNameplate } from '../BroadcastNameplate';
import Label from '../Label';
import { PopupButton } from '../PopupButton';
import { DismissalReason } from './AbstractPopup';
import { showCalendar } from './CalendarPopup';
import { FormPopup } from './FormPopup';
export function ScheduleSessionPopup({ session, reject: rejectProp, resolve, }) {
    const baseSession = useMemo(() => {
        if (session !== undefined) {
            return pick(session, 'id', 'streamId', 'broadcasterPrivateKey', 'broadcasterUserId', 'aesKey');
        }
        const streamId = generateStreamId();
        const { privateKey, userId, aesKey } = generateKeysAndUserId();
        return {
            id: generateSessionId(),
            streamId,
            broadcasterPrivateKey: privateKey,
            broadcasterUserId: userId,
            aesKey,
        };
    }, [session]);
    const [name, setName] = useState(session?.name || '');
    const startsAt = session?.startsAt;
    const [nextStartsAt, setNextStartsAt] = useState(startsAt);
    const nextLocalDate = useMemo(() => new Date(nextStartsAt || 0), [nextStartsAt]);
    const [time, setTime] = useState('');
    const localDateTimeString = `${formatDate(nextLocalDate, 'yyyy-MM-dd')}T${time}`;
    const [isDirty, setIsDirty] = useState(false);
    useEffect(() => {
        const defaultDate = addDays(Date.now(), 1);
        setNextStartsAt(startsAt ?? defaultDate.getTime());
        setTime(formatDate(startsAt || defaultDate, 'HH:mm'));
    }, [startsAt]);
    const validationErrors = useMemo(() => {
        return validateScheduledSession({
            name,
            startsAt: new Date(localDateTimeString).getTime(),
            ...baseSession,
        });
    }, [name, localDateTimeString, baseSession]);
    const isValid = validationErrors.length === 0;
    const nameError = useMemo(() => {
        if (!isDirty)
            return null;
        const nameErrors = validationErrors.filter((issue) => issue.path[0] === 'name');
        if (nameErrors.length === 0)
            return null;
        // Get the first error for the name field
        const error = nameErrors[0];
        switch (error.code) {
            case 'invalid_type':
                return `Invalid input type: expected ${error.expected}`;
            case 'too_small':
                return `The title can not be shorter than ${error.minimum} characters.`;
            case 'too_big':
                return `The title can not be longer than ${error.maximum} characters.`;
            default:
                return 'The title is required';
        }
    }, [validationErrors, isDirty]);
    const dateError = useMemo(() => {
        if (!isDirty)
            return null;
        const dateErrors = validationErrors.filter((issue) => issue.path[0] === 'startsAt');
        if (dateErrors.length === 0)
            return null;
        // Get the first error for the name field
        const error = dateErrors[0];
        switch (error.code) {
            case 'invalid_type':
                return `Invalid input type: expected ${error.expected}`;
            case 'too_small':
                return 'The date can not be in the past.';
            default:
                return 'The date is required';
        }
    }, [validationErrors, isDirty]);
    const scheduleService = ScheduleService.getNotificationInterface();
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const reject = useCallback((reason) => {
        if (!isCalendarOpen) {
            rejectProp(reason);
        }
    }, [rejectProp, isCalendarOpen]);
    const dateControlRef = useRef(null);
    const calendarPopupControllerRef = useRef(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `validationErrors` is just the trigger.
    useEffect(() => {
        calendarPopupControllerRef.current?.refreshAnchors();
    }, [validationErrors]);
    const nameInputId = useId();
    const timeInputId = useId();
    return (_jsx(FormPopup, { reject: reject, title: "Schedule session", canSubmit: isValid, onSubmit: () => {
            setIsDirty(true);
            if (!isValid) {
                return;
            }
            const modifiedSession = {
                name,
                startsAt: new Date(localDateTimeString).getTime(),
                ...baseSession,
            };
            if (session) {
                scheduleService.notifyUpdateScheduledSession(modifiedSession);
            }
            else {
                scheduleService.notifyAddScheduledSession(modifiedSession);
            }
            resolve();
        }, cancelButtonProps: {
            onClick: () => {
                reject(DismissalReason.CloseButton);
            },
        }, okButtonProps: {
            label: session?.id ? 'Update' : 'Create',
        }, children: _jsxs(Body, { children: [_jsx(BroadcastNameplate, { broadcast: {
                        name,
                        startsAt: startsAt ?? Date.now(),
                        ...baseSession,
                    } }), _jsxs("div", { children: [_jsx(Label, { htmlFor: nameInputId, children: "Event title" }), _jsx(TextInput, { id: nameInputId, placeholder: "Untitled event", value: name, onChange: (e) => {
                                setIsDirty(true);
                                setName(e.target.value);
                            }, invalid: isDirty && !!nameError }), isDirty && nameError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), nameError] }))] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "date", children: "Schedule" }), _jsxs(DateTimeControls, { children: [_jsx(Control, { ref: dateControlRef, children: _jsx(DateTimeButton, { type: "button", onClick: () => {
                                            showCalendar(nextLocalDate, {
                                                controllerRef: calendarPopupControllerRef,
                                                anchorRef: dateControlRef,
                                                onDateChange: (newDate) => {
                                                    setNextStartsAt(newDate.getTime());
                                                    setIsDirty(true);
                                                },
                                                onBeforeOpen: () => {
                                                    setIsCalendarOpen(true);
                                                },
                                                onAfterClose: () => {
                                                    setIsCalendarOpen(false);
                                                },
                                            });
                                        }, appendComponent: _jsx(DownCaretIcon, {}), children: formatDate(nextLocalDate, 'LLL d, yyyy') }) }), _jsx(Control, { children: _jsx(TextInput, { id: timeInputId, type: "time", value: time, onChange: (e) => {
                                            setIsDirty(true);
                                            setTime(e.target.value);
                                        } }) })] }), isDirty && dateError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), dateError] }))] })] }) }));
}
const DateTimeButton = styled(PopupButton) `
    min-height: 0;
    height: 3.375rem;
    background-color: var(--tv-ui-color-14);
    box-shadow: 1px 1.5px 4px 0px #0000001A inset,
        1px 1.5px 4px 0px #00000014 inset,
        0px -0.5px 1px 0px #FFFFFF40 inset,
        0px -0.5px 1px 0px #FFFFFF4D inset;

    &:hover {
        background-color: var(--tv-ui-color-14);
        transition-duration: 50ms;
    }

    &:focus {
        box-shadow: 0px 1px 1px rgba(255, 255, 255, 0.3), inset 0px 2px 6px rgba(39, 39, 39, 0.4), 0 0 0 2px rgba(239, 239, 239, 0.3);
    }
`;
const Control = styled.div ``;
const DateTimeControls = styled.div `
    display: grid;
    gap: 0.375rem;

    @media (min-width: 25rem) {
        grid-template-columns: 1fr 1fr;
    }
`;
const Body = styled.div `
    display: grid;
    gap: 2rem;
    padding: 1.5rem;
    padding-top: 0;
`;
const ErrorText = styled.div `
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 0.875rem;
    color: #ffffff;
    margin-top: 0.25rem;
`;
const scheduleSessionPopup = Popup.create(ScheduleSessionPopup);
export function scheduleSession(session, options = {}) {
    executeSoon(async () => {
        options.onOpenPopup?.();
        try {
            await scheduleSessionPopup.pop({ session });
        }
        catch (_ignore) {
        }
        finally {
            options.onClosePopup?.();
        }
    });
}
